import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";
import {
  Minus,
  Plus,
  ArrowLeft,
  ArrowRight,
  X,
  PartyPopper,
  Eye,
  EyeOff,
  Volume2,
  Coins,
} from "lucide-react";
import { t } from "@/i18n";

/* Mêmes bornes que Quiz.jsx : le jeu tient dans l'écran, pas de scroll de
   la page elle-même. */
const NAV_TOP = "4rem";
const NAV_BOTTOM = "calc(3.4rem + env(safe-area-inset-bottom))";

const LEVELS = [
  { id: "single", l: "1 mot" },
  { id: "multi", l: "Plusieurs mots" },
  { id: "full", l: "Phrase entière" },
];

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const shuffle = (arr) => [...arr].sort(() => 0.5 - Math.random());

/* Prononciation via l'API navigateur SpeechSynthesis. */
function speakArabic(text) {
  if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = "ar-SA";
  utter.rate = 0.85;
  window.speechSynthesis.speak(utter);
}

/* Reconstitue la phrase complète et correcte (blancs remplis), pour la
   fiche de découverte et pour le bouton "écouter la phrase". */
function fullSentence(s) {
  return (s.tokens || [])
    .map((tok) => {
      if (!tok.blank) return tok.text;
      const b = (s.bank || []).find((x) => x.correct_index === tok.index);
      return b?.text || "";
    })
    .join(" ");
}

/* Un mot (arabe + phonétique si dispo) — brique réutilisée partout. */
function Word({ text, latin, size = "text-xl" }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`arabic font-bold ${size}`} dir="rtl">
        {text}
      </span>
      {latin && (
        <span className="text-[10px] text-muted-foreground" dir="ltr">
          {latin}
        </span>
      )}
    </div>
  );
}

/* Fiche d'une phrase — phrase complète + traduction, avant de s'entraîner. */
function SentenceCard({ sentence }) {
  if (!sentence) return null;
  const filled = (sentence.tokens || []).map((tok) => {
    if (!tok.blank) return { text: tok.text, latin: tok.latin };
    const b = (sentence.bank || []).find((x) => x.correct_index === tok.index);
    return { text: b?.text || "…", latin: b?.latin };
  });
  return (
    <div className="text-center">
      <div
        className="flex items-center justify-center gap-1.5 flex-wrap mb-2"
        dir="rtl"
      >
        {filled.map((w, i) => (
          <Word key={i} text={w.text} latin={w.latin} size="text-2xl" />
        ))}
        <button
          type="button"
          onClick={() => speakArabic(fullSentence(sentence))}
          aria-label={t("practicesentences.ecouter_la_phrase")}
          dir="ltr"
          className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary transition"
        >
          <Volume2 size={14} />
        </button>
      </div>
      <div className="text-sm text-muted-foreground">
        {sentence.translation_fr || "—"}
      </div>
    </div>
  );
}

/* Écran de découverte — toutes les phrases de la session, avant le jeu. */
function SentencesOverviewScreen({ sentences, onDone, onExit }) {
  return (
    <div
      className="fixed inset-x-0 z-20 bg-background flex flex-col md:static md:z-auto"
      style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
    >
      <div className="shrink-0 px-4 pt-3 pb-2 flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">
            {sentences.length} phrase{sentences.length > 1 ? "s" : ""} {t("practicesentences.decouverte")}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("practicesentences.regarde_ces_phrases_avant_de_t")}
          </p>
        </div>
        <button
          onClick={onExit}
          aria-label={t("alphabetlearnscreen.quitter")}
          className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary transition"
        >
          <X size={15} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 space-y-2">
        {sentences.map((s) => (
          <div key={s.key} className="rounded-2xl border bg-card p-3">
            <SentenceCard sentence={s} />
          </div>
        ))}
        <div className="h-2" />
      </div>
      <div className="shrink-0 border-t bg-background/95 backdrop-blur-xl p-3">
        <button
          onClick={onDone}
          className="w-full max-w-md mx-auto py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
        >
          {t("practicesentences.commencer_l_entrainement")} <ArrowRight size={17} />
        </button>
      </div>
    </div>
  );
}

function Chip({ item, index, disabled, showGloss }) {
  return (
    <Draggable draggableId={item.id} index={index} isDragDisabled={disabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`inline-flex flex-col items-center rounded-xl border bg-card px-2.5 py-1.5 shadow-sm select-none ${
            snapshot.isDragging ? "ring-2 ring-primary" : ""
          } ${disabled ? "opacity-50" : "cursor-grab"}`}
        >
          <span className="arabic text-base font-bold" dir="rtl">
            {item.text}
          </span>
          {/* Phonétique : toujours visible, pas de toggle. */}
          {item.latin && (
            <span className="text-[10px] text-muted-foreground" dir="ltr">
              {item.latin}
            </span>
          )}
          {showGloss && item.gloss && (
            <span className="text-[10px] text-muted-foreground/80">
              {item.gloss}
            </span>
          )}
        </div>
      )}
    </Draggable>
  );
}

export default function PracticeSentences() {
  const navigate = useNavigate();

  // Réglages
  const [level, setLevel] = useState("single");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [count, setCount] = useState(8);
  const [showFiche, setShowFiche] = useState(true);
  const [showGloss, setShowGloss] = useState(false);
  const [avail, setAvail] = useState(0);
  const [masteredWords, setMasteredWords] = useState(0);

  // Session
  const [sentences, setSentences] = useState(null);
  const [overviewSentences, setOverviewSentences] = useState(null);
  const [idx, setIdx] = useState(0);
  const [bank, setBank] = useState([]);
  const [slots, setSlots] = useState({});
  const [checked, setChecked] = useState(false);
  const [correctThisOne, setCorrectThisOne] = useState(null);
  const [lastReward, setLastReward] = useState(0);
  const [results, setResults] = useState([]);
  const [totalDinars, setTotalDinars] = useState(0);
  const [done, setDone] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    api
      .get("/practice/sentence-builder/categories")
      .then((r) => {
        const payload = r.data;
        const next = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.categories)
            ? payload.categories
            : [];
        setCategories(next);
      })
      .catch(() => setCategories([]));
  }, []);

  const loadAvail = async () => {
    try {
      const { data } = await api.get(
        `/progression/sentences?count=1&category=${category}&level=${level}`,
      );
      setAvail(data?.available || 0);
      setMasteredWords(data?.mastered_words || 0);
    } catch {
      setAvail(0);
    }
  };
  useEffect(() => {
    loadAvail();
  }, [category, level]);

  const setupSentence = (s) => {
    setBank(shuffle([...s.bank]));
    setSlots({});
    setChecked(false);
    setCorrectThisOne(null);
    setLastReward(0);
  };

  const start = async () => {
    setNotice("");
    try {
      const { data } = await api.get(
        `/progression/sentences?count=${count}&category=${category}&level=${level}`,
      );
      const list = data?.sentences || [];
      if (list.length === 0) {
        setNotice(
          "Aucune phrase disponible pour l'instant — maîtrise quelques mots de vocabulaire pour en débloquer.",
        );
        return;
      }
      setResults([]);
      setTotalDinars(0);
      setDone(null);
      if (showFiche) {
        setOverviewSentences(list);
        setSentences(null);
      } else {
        setOverviewSentences(null);
        setSentences(list);
        setIdx(0);
        setupSentence(list[0]);
      }
    } catch {
      setNotice("Impossible de charger les phrases pour le moment. Réessaie.");
    }
  };

  const launchFromOverview = () => {
    setSentences(overviewSentences);
    setIdx(0);
    setupSentence(overviewSentences[0]);
    setOverviewSentences(null);
  };

  const backToSettings = () => {
    setSentences(null);
    setOverviewSentences(null);
    setDone(null);
  };

  const current = sentences ? sentences[idx] : null;
  const blankIndices = current
    ? current.tokens.filter((t) => t.blank).map((t) => t.index)
    : [];
  const allFilled = blankIndices.every((i) => slots[i]);

  const correctItemFor = (tokenIndex) =>
    current.bank.find((b) => b.correct_index === tokenIndex);

  const onDragEnd = (result) => {
    if (checked) return;
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const chip =
      bank.find((c) => c.id === draggableId) ||
      Object.values(slots).find((c) => c && c.id === draggableId);
    if (!chip) return;

    if (destination.droppableId === "bank") {
      if (source.droppableId.startsWith("slot-")) {
        const srcIdx = parseInt(source.droppableId.replace("slot-", ""), 10);
        setSlots((s) => ({ ...s, [srcIdx]: null }));
        setBank((b) => [...b, chip]);
      }
      return;
    }

    if (destination.droppableId.startsWith("slot-")) {
      const dstIdx = parseInt(destination.droppableId.replace("slot-", ""), 10);
      if (slots[dstIdx]) return;

      if (source.droppableId === "bank") {
        setBank((b) => b.filter((c) => c.id !== chip.id));
        setSlots((s) => ({ ...s, [dstIdx]: chip }));
      } else if (source.droppableId.startsWith("slot-")) {
        const srcIdx = parseInt(source.droppableId.replace("slot-", ""), 10);
        setSlots((s) => ({ ...s, [srcIdx]: null, [dstIdx]: chip }));
      }
    }
  };

  const verify = async () => {
    const ok = blankIndices.every(
      (i) => slots[i] && slots[i].correct_index === i,
    );
    setCorrectThisOne(ok);
    setChecked(true);
    setResults((r) => [...r, { key: current.key, correct: ok }]);
    try {
      const { data } = await api.post("/progress/answers", {
        items: [
          {
            key: current.key,
            correct: ok,
            skill: "vocabulary",
            options: [],
            answer: 0,
            prompt: "",
            reward_mode: "classique",
          },
        ],
      });
      const earned = data?.dinars_earned || 0;
      setLastReward(earned);
      setTotalDinars((t) => t + earned);
      window.dispatchEvent(
        new CustomEvent("arabdex:dinar-update", { detail: data }),
      );
    } catch {
      setLastReward(0);
    }
  };

  const next = () => {
    if (idx + 1 < sentences.length) {
      const nextIdx = idx + 1;
      setIdx(nextIdx);
      setupSentence(sentences[nextIdx]);
    } else {
      const total = results.length;
      const correct = results.filter((r) => r.correct).length;
      setDone({ total, correct });
    }
  };

  // ── Découverte des phrases ──
  if (overviewSentences) {
    return (
      <SentencesOverviewScreen
        sentences={overviewSentences}
        onDone={launchFromOverview}
        onExit={backToSettings}
      />
    );
  }

  // ── Fin de session ──
  if (done) {
    const score =
      done.total > 0 ? Math.round((done.correct / done.total) * 100) : 0;
    const passed = score >= 70;
    return (
      <div
        className="max-w-xl mx-auto text-center pb-24"
        data-testid="sentences-result"
      >
        <div className="rounded-3xl border bg-card p-7 relative overflow-hidden">
          <PartyPopper size={44} className="mx-auto mb-3 text-amber-500" />
          <h2 className="font-head text-2xl font-bold mb-1">
            {t("quiz.exercice_termine")}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {done.correct} / {done.total} {t("practicesentences.phrases_correctes")} {score}%
          </p>
          <div className="text-5xl font-head font-extrabold text-amber-500 mb-1">
            +{totalDinars} 🪙
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            {totalDinars > 0
              ? t("quiz.gagnes_sur_cet_exercice")
              : t("quiz.aucun_dinar_cette_fois_retente")}
          </p>
          <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-6">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${score}%`,
                backgroundColor: passed ? "#16A34A" : "hsl(var(--destructive))",
              }}
            />
          </div>
          <button
            onClick={backToSettings}
            className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold mb-2.5"
          >
            {t("exerciseblock.continuer")}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={start}
              className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
            >
              <ArrowRight size={15} className="rotate-180" /> {t("myerrors.recommencer")}
            </button>
            <button
              onClick={() => navigate("/practice")}
              className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
            >
              <ArrowLeft size={15} /> {t("coursequestions.accueil")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Jeu — tient sur un seul écran, pas de scroll de page ──
  if (sentences && current) {
    return (
      <div
        className="fixed inset-x-0 z-20 flex flex-col bg-background md:static md:z-auto md:max-w-2xl md:mx-auto md:rounded-2xl md:border md:bg-card"
        style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
      >
        <div className="shrink-0 px-4 pt-3 pb-2 md:px-5 md:pt-5">
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <h1 className="font-head text-lg font-bold">{t("practicesentences.phrases")}</h1>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                <Coins size={13} /> {totalDinars}
              </span>
              <span className="text-xs text-muted-foreground">
                {idx + 1}/{sentences.length}
              </span>
              <button
                onClick={backToSettings}
                aria-label={t("alphabetlearnscreen.quitter")}
                className="w-7 h-7 rounded-full border grid place-items-center hover:bg-secondary transition"
              >
                <X size={13} />
              </button>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${(idx / sentences.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col px-4 md:px-5 py-2 overflow-y-auto">
          <div className="rounded-xl border-2 border-primary/30 bg-primary/5 px-3 py-2 mb-2 flex items-center justify-between gap-2 shrink-0">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                {t("wordcard.traduction")}
              </div>
              <div className="text-sm font-semibold truncate">
                {current.translation_fr || "—"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => speakArabic(fullSentence(current))}
              aria-label={t("practicesentences.ecouter_la_phrase")}
              className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary transition"
            >
              <Volume2 size={14} />
            </button>
          </div>

          <button
            onClick={() => setShowGloss((v) => !v)}
            className="self-end flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground mb-1.5 shrink-0"
          >
            {showGloss ? <EyeOff size={11} /> : <Eye size={11} />}
            {showGloss ? t("practicesentences.masquer_mot_a_mot") : t("practicesentences.traduction_mot_a_mot")}
          </button>

          <DragDropContext onDragEnd={onDragEnd}>
            <div
              className="flex flex-wrap-reverse gap-1.5 rounded-xl bg-secondary/40 p-3 mb-2 shrink-0"
              dir="rtl"
            >
              {current.tokens.map((tok) =>
                tok.blank ? (
                  <Droppable
                    droppableId={`slot-${tok.index}`}
                    key={tok.index}
                    isDropDisabled={checked}
                  >
                    {(provided, snapshot) => (
                      <div className="flex flex-col items-center">
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`min-w-[3rem] min-h-[2.6rem] rounded-xl border-2 border-dashed flex items-center justify-center px-1 ${
                            snapshot.isDraggingOver
                              ? "border-primary bg-primary/10"
                              : "border-muted-foreground/30"
                          } ${
                            checked
                              ? slots[tok.index]?.correct_index === tok.index
                                ? "border-emerald-500 bg-emerald-500/10"
                                : "border-destructive bg-destructive/10"
                              : ""
                          }`}
                        >
                          {slots[tok.index] ? (
                            <Chip
                              item={slots[tok.index]}
                              index={0}
                              disabled={checked}
                              showGloss={showGloss}
                            />
                          ) : (
                            <span className="text-muted-foreground text-xs px-2">
                              …
                            </span>
                          )}
                          <div className="hidden">{provided.placeholder}</div>
                        </div>
                        {checked &&
                          slots[tok.index]?.correct_index !== tok.index && (
                            <div className="mt-0.5 text-[10px] text-emerald-700 dark:text-emerald-400 font-medium text-center">
                              {correctItemFor(tok.index)?.text}
                              {correctItemFor(tok.index)?.latin && (
                                <div
                                  className="text-muted-foreground font-normal"
                                  dir="ltr"
                                >
                                  {correctItemFor(tok.index)?.latin}
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    )}
                  </Droppable>
                ) : (
                  <div key={tok.index} className="px-0.5 py-1">
                    <Word text={tok.text} latin={tok.latin} size="text-base" />
                    {showGloss && tok.gloss && (
                      <div className="text-[10px] text-muted-foreground/80 text-center">
                        {tok.gloss}
                      </div>
                    )}
                  </div>
                ),
              )}
            </div>

            <div className="text-[11px] font-medium text-muted-foreground mb-1 shrink-0">
              {t("practicesentences.mots_disponibles")}
            </div>
            <Droppable droppableId="bank" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="flex flex-wrap gap-1.5 min-h-[3rem] rounded-xl border bg-background p-2.5 shrink-0"
                  dir="rtl"
                >
                  {bank.map((item, i) => (
                    <Chip
                      key={item.id}
                      item={item}
                      index={i}
                      disabled={checked}
                      showGloss={showGloss}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <div className="shrink-0 border-t px-4 py-3 md:px-5">
          {!checked ? (
            <button
              disabled={!allFilled}
              onClick={verify}
              className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition disabled:opacity-40"
            >
              {t("practicesentences.verifier")}
            </button>
          ) : (
            <div className="space-y-2">
              <div
                className={`rounded-xl p-2 text-center text-sm font-semibold flex items-center justify-center gap-2 ${
                  correctThisOne
                    ? "bg-emerald-500/10 text-emerald-600"
                    : "bg-destructive/10 text-destructive"
                }`}
              >
                {correctThisOne
                  ? t("practicesentences.correct")
                  : t("practicesentences.pas_tout_a_fait_reponse_sous")}
                {lastReward > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-600">
                    <Coins size={13} /> +{lastReward}
                  </span>
                )}
              </div>
              <button
                onClick={next}
                className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition flex items-center justify-center gap-2"
              >
                {idx + 1 < sentences.length ? t("alphabetlearnscreen.suivant") : t("practicesentences.voir_mes_resultats")}
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Réglages — même esprit compact que l'écran Vocabulaire ──
  return (
    <div className="max-w-2xl mx-auto pb-40">
      <h1 className="font-head text-2xl font-bold mb-1">{t("practicesentences.phrases")}</h1>
      <p className="text-sm text-muted-foreground mb-5">
        {t("practicesentences.construis_des_phrases_avec_les_mots")}
      </p>

      {notice && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 mb-4 text-sm text-amber-800 dark:text-amber-300">
          {notice}
        </div>
      )}

      <div className="rounded-2xl border bg-card p-4 mb-4 space-y-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
            {t("admin.niveau")}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {LEVELS.map((lv) => (
              <button
                key={lv.id}
                type="button"
                onClick={() => setLevel(lv.id)}
                className={`rounded-xl border-2 p-2.5 text-center transition ${
                  level === lv.id
                    ? "border-primary bg-primary/10"
                    : "hover:border-primary/40"
                }`}
              >
                <div className="text-sm font-semibold leading-tight">
                  {lv.l}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
            {t("practicesentences.categorie")}
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm"
          >
            <option value="all">{t("practicesentences.toutes_les_categories")}</option>
            {categories.map((c) => (
              <option key={c.category} value={c.category}>
                {capitalize(c.category)} ({c.count})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium">{t("practicesentences.phrases")}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCount((c) => Math.max(3, c - 3))}
              className="w-9 h-9 rounded-lg border grid place-items-center"
            >
              <Minus size={15} />
            </button>
            <span className="font-head text-lg font-bold w-8 text-center">
              {count}
            </span>
            <button
              onClick={() => setCount((c) => c + 3)}
              className="w-9 h-9 rounded-lg border grid place-items-center"
            >
              <Plus size={15} />
            </button>
          </div>
        </div>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-sm">
            <span className="font-medium">{t("practicesentences.voir_les_phrases_avant")}</span>
            <span className="block text-xs text-muted-foreground">
              {t("practicesentences.phrase_complete_et_traduction_sur_une")}
            </span>
          </span>
          <input
            type="checkbox"
            checked={showFiche}
            onChange={(e) => setShowFiche(e.target.checked)}
            className="w-5 h-5 accent-primary shrink-0"
          />
        </label>
      </div>

      <div className="text-xs text-muted-foreground mb-4 px-1">
        {avail} phrase{avail > 1 ? "s" : ""} disponible{avail > 1 ? "s" : ""} {t("practicesentences.liees_a_tes")} {masteredWords} mot{masteredWords > 1 ? "s" : ""} {t("practicesentences.maitrise")}
        {masteredWords > 1 ? "s" : ""}
      </div>

      <div
        className="fixed bottom-0 inset-x-0 z-30 border-t bg-background/95 backdrop-blur-xl px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 4.5rem)" }}
      >
        <div className="max-w-2xl mx-auto">
          <button
            disabled={avail === 0}
            onClick={start}
            className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {avail === 0 ? t("practicesentences.aucune_phrase_disponible") : t("dailyplanintro.commencer")}{" "}
            <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
